import React, { useEffect, useMemo, useRef, useState } from "react";
import KanbanColumn from "./KanbanColumn";
import { CaretLeftOutlined, CaretRightOutlined } from "@ant-design/icons";
import { DndContext, closestCorners, DragOverlay } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import TaskCard from "./TaskCard";
import apiClient from "utils/apiClient";
import { PM_SERVICE_BASE_URL } from "constants";
import { Button } from "antd";

const columns = [
  { key: "BACKLOG", title: "Backlog", dot: "#F59E0B" },
  { key: "OPEN", title: "Open", dot: "#8B5CF6" },
  { key: "IN_PROGRESS", title: "In Progress", dot: "#3B82F6" },
  { key: "BLOCKED", title: "Blocked", dot: "#F59E0B" },
  { key: "RESOLVED", title: "Resolved", dot: "#10B981" },
  { key: "COMPLETED", title: "Completed", dot: "#10B981" },
  { key: "CANCELLED", title: "Abandoned", dot: "#F59E0B" },
];

const KanbanBoard = ({
  tasks,
  setTasks,
  fetchTasksForColumn,
  hasMoreByStatus,
  onTaskClick,
  onEditTask,
  onCloneTask,
  onTransferTask,
  onDelete,
  onAssigneeClick,
  setSelectedTask,
  setIsTransferDrawerOpen,
  isMultiSelectMode,
  selectedTaskIds,
  onToggleSelect,
}) => {
  const [activeTask, setActiveTask] = useState(null);

  const isDownRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);

  // scroll
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;

    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();
    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, []);

  const COLUMN_WIDTH = 252;
  const COLUMN_GAP = 21;
  const SCROLL_AMOUNT = COLUMN_WIDTH + COLUMN_GAP;

  const scroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;

    el.scrollBy({
      left: direction === "left" ? -SCROLL_AMOUNT : SCROLL_AMOUNT,
      behavior: "smooth",
    });
  };

  const handlePointerDown = (e) => {
    // only left click
    if (e.button !== 0) return;

    // don't activate if clicking a draggable task
    if (e.target.closest(".task-card")) return;

    const el = scrollRef.current;
    if (!el) return;

    isDownRef.current = true;
    startXRef.current = e.pageX;
    scrollLeftRef.current = el.scrollLeft;

    el.style.cursor = "grabbing";
  };

  const handlePointerMove = (e) => {
    if (!isDownRef.current) return;

    const el = scrollRef.current;
    if (!el) return;

    const walk = e.pageX - startXRef.current;
    el.scrollLeft = scrollLeftRef.current - walk;
  };

  const handlePointerUp = () => {
    const el = scrollRef.current;
    if (!el) return;

    isDownRef.current = false;
    el.style.cursor = "grab";
  };

  const getTasksByStatus = (status) => tasks.filter((t) => t.status === status);

  const handleDragStart = (event) => {
    const task = tasks.find((t) => t.id === event.active.id);
    setActiveTask(task);
  };

  const updateTaskStatus = (taskId, newStatus) => {
    setTasks((prev) =>
      prev.map((task) =>
        task.id === taskId ? { ...task, status: newStatus } : task,
      ),
    );
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over) {
      setActiveTask(null);
      return;
    }

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) {
      setActiveTask(null);
      return;
    }

    const activeTask = tasks.find((t) => t.id === activeId);
    const overTask = tasks.find((t) => t.id === overId);

    let newStatus = activeTask.status;

    // reorder inside same column
    if (overTask && activeTask.status === overTask.status) {
      const columnTasks = tasks.filter((t) => t.status === activeTask.status);

      const oldIndex = columnTasks.findIndex((t) => t.id === activeId);
      const newIndex = columnTasks.findIndex((t) => t.id === overId);

      const reorderedColumn = arrayMove(columnTasks, oldIndex, newIndex);

      setTasks((prev) => {
        const otherTasks = prev.filter((t) => t.status !== activeTask.status);

        return [...otherTasks, ...reorderedColumn];
      });
    }

    // move to different column (drop on card)
    else if (overTask && activeTask.status !== overTask.status) {
      newStatus = overTask.status;
      setTasks((prev) =>
        prev.map((t) => (t.id === activeId ? { ...t, status: newStatus } : t)),
      );
    }

    // dropped directly on empty column
    else if (columns.some((c) => c.key === overId)) {
      newStatus = overId;
      setTasks((prev) =>
        prev.map((t) => (t.id === activeId ? { ...t, status: newStatus } : t)),
      );
    }

    setActiveTask(null);

    // sync with backend
    try {
      await apiClient.patch(
        `${PM_SERVICE_BASE_URL}/api/v1/projects/${activeTask.projectId}/tasks/${activeTask.id}/status`,
        null,
        {
          params: { status: newStatus },
        },
      );
    } catch (err) {
      console.error("Failed to update task status:", err);
      // rollback UI change on failure
      setTasks((prev) => prev.map((t) => (t.id === activeId ? activeTask : t)));
    }
  };

  return (
    <DndContext
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      autoScroll={{
        threshold: {
          x: 0.2,
          y: 0.2,
        },
      }}
    >
      {isMultiSelectMode && selectedTaskIds.length > 0 && (
        <div
          style={{
            background: "#f0f7ff",
            border: "1px solid #d6e4ff",
            padding: "10px 16px",
            marginBottom: 12,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderRadius: 6,
          }}
        >
          <span className="text-sm">{selectedTaskIds.length} task(s) selected</span>

          <Button
            type="primary"
            onClick={() => {
              setSelectedTask(null);
              setIsTransferDrawerOpen(true);
            }}
            style={{fontSize:12}}
          >
            Move Selected Tasks
          </Button>
        </div>
      )}
      <div className="kanban-board-wrapper">
        {canScrollLeft && (
          <button className="scroll-btn left" onClick={() => scroll("left")}>
            <CaretLeftOutlined />
          </button>
        )}

        <div
          className="kanban-board-custom"
          ref={scrollRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        >
          {columns.map((col) => {
            const columnTasks = getTasksByStatus(col.key);

            return (
              <SortableContext
                key={col.key}
                items={columnTasks.map((t) => t.id)}
                strategy={verticalListSortingStrategy}
              >
                <KanbanColumn
                  column={col}
                  tasks={columnTasks}
                  fetchMoreTasks={() => fetchTasksForColumn(col.key)}
                  hasMore={hasMoreByStatus[col.key] ?? true}
                  onTaskClick={onTaskClick}
                  onEditTask={onEditTask}
                  onCloneTask={onCloneTask}
                  onTransferTask={onTransferTask}
                  onDelete={onDelete}
                  onAssigneeClick={onAssigneeClick}
                  isMultiSelectMode={isMultiSelectMode}
                  selectedTaskIds={selectedTaskIds}
                  onToggleSelect={onToggleSelect}
                />
              </SortableContext>
            );
          })}
        </div>

        {canScrollRight && (
          <button className="scroll-btn right" onClick={() => scroll("right")}>
            <CaretRightOutlined />
          </button>
        )}
      </div>

      {/* drag overlay */}
      <DragOverlay>
        {activeTask && <TaskCard task={activeTask} isDragging />}
      </DragOverlay>
    </DndContext>
  );
};

export default KanbanBoard;
